/**
 * Kontrastsichere Neben- und UI-Farben für Banner und Settings-Modal.
 *
 * Banner und Modal übernehmen Hinter-/Vordergrund aus den Design-
 * Einstellungen (helle wie dunkle Templates). Feste Grautöne (#666, #ccc,
 * rgba(0,0,0,…)) oder per opacity abgeschwächter Text funktionieren dabei nur
 * auf hellem Grund - auf dunklem werden sie unlesbar (WCAG 1.4.3) bzw.
 * Schalter-Umrisse unsichtbar (WCAG 1.4.11). Alle abgeleiteten Farben werden
 * deshalb hier aus Text- und Hintergrundfarbe gemischt und gegen die
 * Mindestkontraste geprüft.
 */
export class ThemeColors {
    /** WCAG 1.4.3 AA für normalen Text */
    static TEXT_CONTRAST = 4.5;

    /** WCAG 1.4.11 für Bedienelemente (Schalter-Spur, Rahmen) */
    static UI_CONTRAST = 3;

    /**
     * Liest #rgb, #rgba, #rrggbb, #rrggbbaa, rgb() und rgba().
     * @returns {{r:number,g:number,b:number,a:number}|null}
     */
    static parse(color) {
        if (typeof color !== 'string') return null;
        const value = color.trim().toLowerCase();

        const hex = value.match(/^#([0-9a-f]{3,8})$/);
        if (hex) {
            let h = hex[1];
            if (h.length === 3 || h.length === 4) h = h.split('').map(c => c + c).join('');
            if (h.length !== 6 && h.length !== 8) return null;
            return {
                r: parseInt(h.slice(0, 2), 16),
                g: parseInt(h.slice(2, 4), 16),
                b: parseInt(h.slice(4, 6), 16),
                a: h.length === 8 ? parseInt(h.slice(6, 8), 16) / 255 : 1
            };
        }

        const rgb = value.match(/^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)(?:[\s,/]+([\d.]+%?))?\s*\)$/);
        if (rgb) {
            let a = rgb[4] === undefined ? 1 : parseFloat(rgb[4]);
            if (rgb[4] && rgb[4].endsWith('%')) a /= 100;
            return { r: +rgb[1], g: +rgb[2], b: +rgb[3], a: Math.min(1, Math.max(0, a)) };
        }

        if (value === 'white') return { r: 255, g: 255, b: 255, a: 1 };
        if (value === 'black') return { r: 0, g: 0, b: 0, a: 1 };
        return null;
    }

    static toHex({ r, g, b }) {
        return '#' + [r, g, b].map(c => Math.round(c).toString(16).padStart(2, '0')).join('');
    }

    /** Mischt a mit b; t=0 → a, t=1 → b. */
    static mixRgb(a, b, t) {
        return {
            r: a.r + (b.r - a.r) * t,
            g: a.g + (b.g - a.g) * t,
            b: a.b + (b.b - a.b) * t,
            a: 1
        };
    }

    /** Halbtransparente Farbe auf deckenden Hintergrund legen. */
    static flatten(color, background) {
        if (!color || color.a >= 1 || !background) return color;
        return ThemeColors.mixRgb(background, color, color.a);
    }

    static luminance({ r, g, b }) {
        const lin = c => {
            const s = c / 255;
            return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
        };
        return 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
    }

    static contrastRgb(a, b) {
        const l1 = ThemeColors.luminance(a);
        const l2 = ThemeColors.luminance(b);
        return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
    }

    /** Kontrastverhältnis zweier CSS-Farben, null wenn nicht auswertbar. */
    static contrast(foreground, background) {
        const bg = ThemeColors.flatten(ThemeColors.parse(background), { r: 255, g: 255, b: 255, a: 1 });
        const fg = ThemeColors.flatten(ThemeColors.parse(foreground), bg);
        return fg && bg ? ThemeColors.contrastRgb(fg, bg) : null;
    }

    /**
     * Mischt die Textfarbe um `amount` Richtung Hintergrund (abgeschwächter
     * Nebentext statt opacity) - aber nur so weit, dass `minRatio` erhalten
     * bleibt. Reicht schon die Textfarbe selbst nicht, bleibt sie unverändert.
     */
    static muted(text, background, amount = 0.3, minRatio = ThemeColors.TEXT_CONTRAST) {
        const bg = ThemeColors.flatten(ThemeColors.parse(background), { r: 255, g: 255, b: 255, a: 1 });
        const fg = ThemeColors.flatten(ThemeColors.parse(text), bg);
        if (!fg || !bg) return text;

        for (let t = amount; t > 0.001; t -= 0.05) {
            const candidate = ThemeColors.mixRgb(fg, bg, t);
            if (ThemeColors.contrastRgb(candidate, bg) >= minRatio) {
                return ThemeColors.toHex(candidate);
            }
        }
        return text;
    }

    /**
     * Gibt `color` zurück, wenn sie auf `background` `minRatio` erreicht -
     * sonst die schrittweise Richtung Weiß bzw. Schwarz aufgehellte/
     * abgedunkelte Variante, die ihn erreicht (Farbton bleibt erkennbar).
     */
    static readable(color, background, minRatio = ThemeColors.TEXT_CONTRAST, fallback = null) {
        const bg = ThemeColors.flatten(ThemeColors.parse(background), { r: 255, g: 255, b: 255, a: 1 });
        const fg = ThemeColors.flatten(ThemeColors.parse(color), bg);
        if (!fg || !bg) return fallback || color;
        if (ThemeColors.contrastRgb(fg, bg) >= minRatio) return color;

        const target = ThemeColors.luminance(bg) > 0.18
            ? { r: 0, g: 0, b: 0, a: 1 }
            : { r: 255, g: 255, b: 255, a: 1 };
        for (let t = 0.1; t <= 1.0001; t += 0.1) {
            const candidate = ThemeColors.mixRgb(fg, target, t);
            if (ThemeColors.contrastRgb(candidate, bg) >= minRatio) {
                return ThemeColors.toHex(candidate);
            }
        }
        return fallback || color;
    }

    /**
     * Abgeleitete Palette für ein design-Objekt aus pixelmateConfig.
     */
    static forDesign(design = {}) {
        const background = design.colors?.background || '#ffffff';
        const text = design.colors?.text || '#333333';
        const accent = design.buttons?.accept?.background || '#0073aa';

        return {
            background,
            text,
            // Nebentexte (Beschreibungen, Laufzeiten, "(Erforderlich)")
            muted: ThemeColors.muted(text, background, 0.3),
            // Schalter-Spur "aus", Aufklapp-Pfeile: Bedienelement, ≥3:1
            control: ThemeColors.muted(text, background, 0.45, ThemeColors.UI_CONTRAST),
            // Rein dekorative Trennlinien - passend zu hellem wie dunklem Grund
            border: ThemeColors.muted(text, background, 0.85, 1),
            // Leichte Flächen (Chips, Cookie-Karten, Hover)
            subtle: 'rgba(128, 128, 128, 0.12)',
            // Akzentfarbe als Schrift (aktiver Tab) auf dem Hintergrund
            accentText: ThemeColors.readable(accent, background, ThemeColors.TEXT_CONTRAST, text),
            error: ThemeColors.readable('#c00000', background, ThemeColors.TEXT_CONTRAST, text)
        };
    }
}
